/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { useState, useCallback } from 'preact/compat';
import { setQuoteTemplateExpirationDate } from '@/quote-management/api';

export interface UseExpirationDateParams {
  templateId?: string;
  initialExpirationDate?: string;
  expirationDateRequiredErrorMessage?: string;
}

export interface UseExpirationDateReturn {
  /**
   * Whether the expiration date modal is currently open.
   */
  isExpirationModalOpen: boolean;
  /**
   * The new expiration date entered by the user (in YYYY-MM-DD format).
   */
  expirationDate: string;
  /**
   * Error message for expiration date operation.
   */
  expirationError: string;
  /**
   * Error message for expiration date validation.
   */
  expirationDateError: string;
  /**
   * Success message for expiration date operation.
   */
  expirationSuccess: string;
  /**
   * Opens the expiration date modal and initializes state with the current expiration date.
   */
  handleExpirationClick: (currentExpirationDate?: string) => void;
  /**
   * Closes the expiration date modal and resets all state.
   */
  handleExpirationClose: () => void;
  /**
   * Updates the expiration date and clears any date-related errors.
   */
  handleExpirationDateChange: (value: string) => void;
  /**
   * Validates and saves the expiration date. Makes an API call to save immediately.
   * Closes the modal if successful.
   * Returns a promise that resolves to true if successful, false otherwise.
   */
  handleExpirationSave: () => Promise<boolean>;
  /**
   * Resets expiration date state (useful after successful submission).
   */
  resetExpirationState: () => void;
}

/**
 * Custom hook to manage quote template expiration date functionality.
 * Handles state management for the expiration date modal, validation, and API calls.
 * Saves the expiration date immediately when user clicks "Save" in the modal.
 */
export const useExpirationDate = (
  params: UseExpirationDateParams = {}
): UseExpirationDateReturn => {
  const { templateId, initialExpirationDate, expirationDateRequiredErrorMessage = 'Expiration date is required' } = params;

  const [isExpirationModalOpen, setIsExpirationModalOpen] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<string>('');
  const [expirationError, setExpirationError] = useState<string>('');
  const [expirationDateError, setExpirationDateError] = useState<string>('');
  const [expirationSuccess, setExpirationSuccess] = useState<string>('');

  const handleExpirationClick = useCallback((currentExpirationDate?: string) => {
    setExpirationDate(currentExpirationDate || initialExpirationDate || '');
    setExpirationError('');
    setExpirationDateError('');
    setExpirationSuccess('');
    setIsExpirationModalOpen(true);
  }, [initialExpirationDate]);

  const handleExpirationClose = useCallback(() => {
    setIsExpirationModalOpen(false);
    setExpirationDate('');
    setExpirationError('');
    setExpirationDateError('');
    setExpirationSuccess('');
  }, []);

  const handleExpirationDateChange = useCallback((value: string) => {
    setExpirationDate(value);
    if (value.trim()) {
      setExpirationDateError('');
    }
  }, []);

  const handleExpirationSave = useCallback(async (): Promise<boolean> => {
    // Validate expiration date
    if (!expirationDate.trim()) {
      setExpirationDateError(expirationDateRequiredErrorMessage);
      return false;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(expirationDate)) {
      setExpirationDateError('Please enter a valid date');
      return false;
    }

    // Validate that the date is in the future
    const selectedDate = new Date(expirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setExpirationDateError('Expiration date must be in the future');
      return false;
    }

    // Validate that templateId is provided
    if (!templateId) {
      setExpirationError('Template ID is required');
      return false;
    }

    // Clear any previous errors
    setExpirationError('');
    setExpirationDateError('');
    
    // Call API to save expiration date immediately
    try {
      await setQuoteTemplateExpirationDate({
        templateId,
        expirationDate,
      });
      
      // Close modal on success
      setIsExpirationModalOpen(false);
      setExpirationSuccess('Expiration date saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to set expiration date:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to set expiration date. Please try again.';
      setExpirationError(errorMessage);
      return false;
    }
  }, [expirationDate, expirationDateRequiredErrorMessage, templateId]);

  const resetExpirationState = useCallback(() => {
    setExpirationDate('');
    setExpirationError('');
    setExpirationDateError('');
    setExpirationSuccess('');
  }, []);

  return {
    isExpirationModalOpen,
    expirationDate,
    expirationError,
    expirationDateError,
    expirationSuccess,
    handleExpirationClick,
    handleExpirationClose,
    handleExpirationDateChange,
    handleExpirationSave,
    resetExpirationState,
  };
};

